/**
 * 房间选择模块
 */

import React, { memo, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import { Button } from 'antd';
import intl from 'react-intl-universal';

import { createStateSelector, findItemByKey } from '@/redux/actions/reservation/helper';
import {
  handleAutoAssign,
  batchUpdateRoomPrice,
  batchUpdateRoomCoupon,
  getMainState,
} from '@/redux/actions/reservation/createReserve';
import { ReducerState, storeName } from '@/redux/reducers/reservation/createReserve';

import ContentBlock from '@/components/Reservation/ContentBlock';
import BatchModifyPriceModal, {
  BatchModifyPriceModalRef,
} from '@/components/Reservation/BatchModifyPriceModal';
import BatchCouponButton, {
  IBatchCouponParams,
} from '@/components/Reservation/BatchSelectCouponButton';

import RoomList from '../RoomList';
import RentRoomList from '../RentRoomList';
import styles from './index.module.scss';

const stateSelector = (state: ReducerState) => {
  const { rentRoomInfos, customerInfo, channelList, channelInfo } = state;
  return {
    rentRoomInfos,
    mainUserId: customerInfo.wyuId,
    channelList,
    sourceChannelId: channelInfo.channelId,
  };
};
const mapStateToProps = createStateSelector<ReducerState>(storeName, stateSelector);
const mapDispatchToProps = {
  callAutoAssign: handleAutoAssign,
  callBatchUpdatePrice: batchUpdateRoomPrice,
  callBatchUpdateCoupon: batchUpdateRoomCoupon,
  callGetState: getMainState,
};

type MapStateProps = ReturnType<typeof stateSelector>;
type MapDispatchProps = typeof mapDispatchToProps;

const Index: React.FC<MapStateProps & MapDispatchProps> = (props) => {
  const batchModalRef = useRef<BatchModifyPriceModalRef>();
  const {
    callAutoAssign,
    callBatchUpdatePrice,
    callBatchUpdateCoupon,
    callGetState,
    rentRoomInfos,
    channelList,
    sourceChannelId,
    mainUserId,
  } = props;

  // 当前渠道类型
  const channelType = useMemo(() => {
    const channelItem = findItemByKey(channelList, 'id', sourceChannelId);
    return channelItem?.type;
  }, [channelList, sourceChannelId]);

  // 批量填写价格
  const showBatchPriceModal = () => {
    batchModalRef.current.openModal(rentRoomInfos, 0);
  };

  // 一键选券参数
  const getCouponParam = () => {
    return new Promise<IBatchCouponParams>((resolve) => {
      callGetState((state) => {
        const { commonInfo } = state;
        const { occType } = commonInfo;
        resolve({
          extra: {
            wyuid: mainUserId,
            couponNos: [],
          },
          rooms: rentRoomInfos.map((room) => {
            return {
              indexId: room.indexId,
              roomId: room.roomId,
              roomNo: room.roomNo,
              roomTypeId: room.roomTypeId,
              roomTypeName: room.roomTypeName,
              roomPriceList: room.roomPriceList.map((dayPrice) => ({
                key: dayPrice.rentDateId,
                occType,
                rentDate: dayPrice.rentDate,
                roomPrice: dayPrice.inputPrice,
                couponNo: dayPrice.couponNo,
              })),
            };
          }),
        });
      });
    });
  };

  return (
    <ContentBlock
      title={intl.get('reservation_room_block_title')}
      className={styles.selectorContainer}
      extra={
        <div className={styles.extraButtonList}>
          <BatchCouponButton
            userId={mainUserId}
            channelType={channelType}
            getCouponParam={getCouponParam}
            onChange={callBatchUpdateCoupon}
          />
          <Button className={styles.spaceInRight} onClick={showBatchPriceModal}>
            {intl.get('reservation_field_room_batch_price_btn')}
          </Button>
          <Button onClick={() => callAutoAssign()}>
            {intl.get('reservation_room_auto_assign')}
          </Button>
        </div>
      }
    >
      <div className={styles.contentContainer}>
        <div className={styles.contentListWrapper}>
          <RoomList />
        </div>
        <div className={styles.contentSelectWrapper}>
          <RentRoomList />
        </div>
      </div>
      <BatchModifyPriceModal innerRef={batchModalRef} onEnd={callBatchUpdatePrice} />
    </ContentBlock>
  );
};

export default connect<MapStateProps, MapDispatchProps>(
  mapStateToProps,
  mapDispatchToProps,
)(memo(Index));
